import type { ReactNode } from "react";
import { parseArticleBody, type ArticleInline } from "@care-by-ella/contracts";

// CAR-119/120 — the article editor's live preview, rendered as React nodes
// (never `dangerouslySetInnerHTML`).
//
// TES-277: the grammar now comes from the shared parseArticleBody in
// @care-by-ella/contracts — the same parser the mobile app renders with, so
// the preview can no longer drift from what the patient sees. Two behavior
// changes vs the old hand-rolled version, both deliberate:
//   - checklists (`- [ ]` / `- [x]`) now render as real checkboxes instead
//     of the literal text "[ ] …" (the old preview silently mis-rendered
//     what mobile shows correctly);
//   - italic / inline code / links / ordered lists are gone — dead grammar
//     the WYSIWYG cannot author and no surface renders; previewing them
//     misled authors into writing syntax patients would see as literals.

function renderInline(content: ArticleInline[]): ReactNode[] {
  return content.map((seg, i) =>
    seg.bold ? <strong key={i}>{seg.text}</strong> : seg.text,
  );
}

export function MarkdownPreview({ source }: { source: string }) {
  if (!source.trim()) {
    return (
      <span className="text-xs text-textSecondary italic">Ingen text.</span>
    );
  }

  const blocks = parseArticleBody(source);

  return (
    <div className="flex flex-col gap-sm">
      {blocks.map((block, b) => {
        switch (block.kind) {
          case "image":
            return (
              <img
                key={b}
                src={`/public-api/v1/media/${encodeURIComponent(block.mediaKey)}`}
                alt={block.alt}
                loading="lazy"
                className="max-w-full rounded-md"
              />
            );
          case "heading": {
            const cls =
              block.level === 1
                ? "text-lg font-semibold"
                : block.level === 2
                  ? "text-base font-semibold"
                  : "text-sm font-semibold";
            return (
              <p key={b} className={cls}>
                {renderInline(block.content)}
              </p>
            );
          }
          case "bullets":
            return (
              <ul
                key={b}
                className="list-disc pl-lg text-sm flex flex-col gap-xs"
              >
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case "checklist":
            return (
              <ul key={b} className="text-sm flex flex-col gap-xs">
                {block.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-xs">
                    <input
                      type="checkbox"
                      checked={item.checked}
                      readOnly
                      className="mt-[3px] accent-brand"
                    />
                    <span>{renderInline(item.content)}</span>
                  </li>
                ))}
              </ul>
            );
          case "cta":
            // Legacy web convention; not produced without the legacy flag,
            // but degrade visibly rather than dropping content.
            return (
              <p key={b} className="text-sm font-semibold">
                {block.label} →
              </p>
            );
          case "paragraph":
          default:
            return (
              <p key={b} className="text-sm leading-relaxed">
                {renderInline(
                  block.lines.flatMap((line, i) =>
                    i === 0 ? line : [{ text: " ", bold: false }, ...line],
                  ),
                )}
              </p>
            );
        }
      })}
    </div>
  );
}
